import React, { useEffect, useState, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { DOCLINK_COLORS } from "@/config/colors";
import { normalizePractitionerFromResponse } from "@/utils/hubMedApiNormalize";

const HubMedPractitionerDrawer = ({
  open,
  onClose,
  doctorId,
  showAlert,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [row, setRow] = useState(null);
  const [isCannabis, setIsCannabis] = useState(false);
  const [isADHD, setIsADHD] = useState(false);
  const [isADHD_NSW, setIsADHD_NSW] = useState(false);

  useEffect(() => {
    if (!open || !doctorId) {
      setRow(null);
      setLoadError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await phappsRequest(
          API_PATHS.HUBMED_ADMIN_PRACTITIONER_DETAILS,
          "GET",
          null,
          { doctorId }
        );
        const p = normalizePractitionerFromResponse(
          data?.practitioner ?? data?.Practitioner ?? data
        );
        if (!cancelled && p) {
          setRow(p);
          setIsCannabis(!!p.isCannabis);
          setIsADHD(!!p.isADHD);
          setIsADHD_NSW(!!p.isADHD_NSW);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(getAdminApiErrorMessage(err, "Could not load practitioner"));
          setRow(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, doctorId]);

  const save = useCallback(async () => {
    if (!doctorId) return;
    setSaving(true);
    try {
      const data = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_UPDATE_PRACTITIONER_FLAGS,
        "POST",
        {
          doctorId,
          isCannabis,
          isADHD,
          isADHD_NSW,
        }
      );
      const updated = normalizePractitionerFromResponse(
        data?.practitioner ?? data?.Practitioner ?? data
      );
      if (updated) {
        setRow(updated);
        onSaved?.(updated);
        showAlert?.({ severity: "success", message: "Flags saved." });
        onClose?.();
      }
    } catch (err) {
      showAlert?.({
        severity: "error",
        message: getAdminApiErrorMessage(err, "Could not save flags"),
      });
    } finally {
      setSaving(false);
    }
  }, [doctorId, isCannabis, isADHD, isADHD_NSW, onSaved, onClose, showAlert]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 12,
          borderBottomLeftRadius: 12,
          maxWidth: "100vw",
          width: { xs: "100vw", sm: 420 },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "grey.50",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}>
            Edit practitioner
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={36} sx={{ color: DOCLINK_COLORS.MID_PURPLE }} />
            </Box>
          ) : loadError ? (
            <Alert severity="error">{loadError}</Alert>
          ) : row ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Alert severity="info" sx={{ fontSize: 13 }}>
                Practitioner identity details are synced from Halaxy. Only internal category flags
                can be edited here.
              </Alert>

              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  p: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={800}>
                  {row.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Practitioner ID: {row.practitionerId ?? "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Halaxy profile ID: {row.halaxyProfileId ?? "—"}
                </Typography>
              </Box>

              <Divider />

              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                Internal flags
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isCannabis}
                    onChange={(e) => setIsCannabis(e.target.checked)}
                    color="primary"
                  />
                }
                label="Cannabis"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isADHD}
                    onChange={(e) => setIsADHD(e.target.checked)}
                    color="primary"
                  />
                }
                label="ADHD"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isADHD_NSW}
                    onChange={(e) => setIsADHD_NSW(e.target.checked)}
                    color="primary"
                  />
                }
                label="ADHD NSW"
              />

              <Box sx={{ display: "flex", gap: 1, pt: 2 }}>
                <Button variant="outlined" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={save}
                  disabled={saving}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: DOCLINK_COLORS.MID_PURPLE,
                  }}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
};

export default HubMedPractitionerDrawer;
